import { Injectable } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  IHttpConnectionOptions,
  LogLevel
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface PremiacionWsChangedEvent {
  exposicionId: number;
  payload: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class PremiacionWsDebugService {
  private readonly reconnectDelaysMs = [0, 2000, 5000, 10000];
  private readonly debugEnabled = !!environment.premiacionWsDebugEnabled;
  private readonly changedSubject = new Subject<PremiacionWsChangedEvent>();
  private connection: HubConnection | null = null;
  private joinedExpoId: number | null = null;
  private stopInFlight: Promise<void> | null = null;
  public readonly changed$: Observable<PremiacionWsChangedEvent> = this.changedSubject.asObservable();

  constructor(private readonly authService: AuthService) {}

  public isConnectedToExpo(exposicionId: number | null = null): boolean {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return false;
    }

    if (!this.joinedExpoId) {
      return false;
    }

    const targetExpoId = this.normalizeExpoId(exposicionId);
    if (!targetExpoId) {
      return true;
    }

    return this.joinedExpoId === targetExpoId;
  }

  public async start(exposicionId: number): Promise<void> {
    const nextExpoId = this.normalizeExpoId(exposicionId);
    if (!nextExpoId) {
      return;
    }

    this.ensureConnection();
    if (!this.connection) {
      return;
    }

    const connected = await this.ensureConnected();
    if (!connected) {
      return;
    }

    if (this.joinedExpoId === nextExpoId) {
      return;
    }

    if (this.joinedExpoId) {
      await this.leaveExpo(this.joinedExpoId);
    }

    await this.joinExpo(nextExpoId);
  }

  public async switchExpo(exposicionId: number): Promise<void> {
    const nextExpoId = this.normalizeExpoId(exposicionId);
    if (!nextExpoId) {
      return;
    }

    if (!this.connection) {
      await this.start(nextExpoId);
      return;
    }

    const connected = await this.ensureConnected();
    if (!connected) {
      return;
    }

    if (this.joinedExpoId === nextExpoId) {
      return;
    }

    if (this.joinedExpoId) {
      await this.leaveExpo(this.joinedExpoId);
    }

    await this.joinExpo(nextExpoId);
  }

  public async stop(): Promise<void> {
    if (this.stopInFlight) {
      return this.stopInFlight;
    }

    const stopPromise = this.stopInternal();
    this.stopInFlight = stopPromise;

    try {
      await stopPromise;
    } finally {
      if (this.stopInFlight === stopPromise) {
        this.stopInFlight = null;
      }
    }
  }

  private async stopInternal(): Promise<void> {
    const connection = this.connection;
    const joinedExpoId = this.joinedExpoId;
    this.connection = null;
    this.joinedExpoId = null;

    if (!connection) {
      return;
    }

    if (connection.state === HubConnectionState.Connected && joinedExpoId) {
      try {
        await connection.invoke('LeaveExpo', joinedExpoId);
        this.logDebug(`left expo ${joinedExpoId}`);
      } catch (error) {
        if (!this.isConnectionClosedInvocationError(error)) {
          this.logError(`LeaveExpo(${joinedExpoId}) failed`, error);
        }
      }
    }

    if (connection.state === HubConnectionState.Disconnected) {
      return;
    }

    try {
      await connection.stop();
    } catch (error) {
      this.logError('stop failed', error);
    }
  }

  private ensureConnection(): void {
    if (this.connection) {
      return;
    }

    const hubUrl = this.resolveHubUrl();
    const connectionOptions = this.buildConnectionOptions();
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, connectionOptions)
      .configureLogging(LogLevel.None)
      .withAutomaticReconnect(this.reconnectDelaysMs)
      .build();

    connection.onreconnecting((error) => {
      this.logDebug('reconnecting', error);
    });

    connection.onreconnected((connectionId) => {
      this.logDebug('reconnected', connectionId);

      const rejoinExpoId = this.joinedExpoId;
      if (!rejoinExpoId) {
        return;
      }

      void connection
        .invoke('JoinExpo', rejoinExpoId)
        .then(() => {
          // Igual que en pista-live, al volver a unirnos forzamos una resincronizacion
          // para recuperar premios que pudieron haberse emitido durante la desconexion.
          this.changedSubject.next({
            exposicionId: rejoinExpoId,
            payload: null
          });
        })
        .catch((error) => {
          this.logError(`rejoin expo ${rejoinExpoId} failed`, error);
        });
    });

    connection.onclose((error) => {
      this.logDebug('closed', error);
    });

    connection.on('premiacion.changed', (payload: unknown) => {
      const exposicionId = this.joinedExpoId;
      if (exposicionId) {
        this.changedSubject.next({
          exposicionId,
          payload
        });
      }
      if (this.debugEnabled) {
        console.log('[premiacion.changed]', payload);
      }
    });

    this.connection = connection;
  }

  private buildConnectionOptions(): IHttpConnectionOptions {
    const options: IHttpConnectionOptions = {
      accessTokenFactory: () => this.authService.getAccessToken(),
      withCredentials: false
    };

    if (this.isDirectWebSocketEnabled()) {
      options.transport = HttpTransportType.WebSockets;
      options.skipNegotiation = true;
    }

    return options;
  }

  private isDirectWebSocketEnabled(): boolean {
    return !!environment.FEATURES?.['signalrDirectWebSocket'];
  }

  private async ensureConnected(): Promise<boolean> {
    if (!this.connection) {
      return false;
    }

    if (this.connection.state === HubConnectionState.Connected) {
      return true;
    }

    if (this.connection.state !== HubConnectionState.Disconnected) {
      return false;
    }

    try {
      await this.connection.start();
      this.logDebug('connected', this.connection.connectionId);
      return true;
    } catch (error) {
      this.logError('start failed', error);
      return false;
    }
  }

  private async joinExpo(exposicionId: number): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('JoinExpo', exposicionId);
      this.joinedExpoId = exposicionId;
      this.logDebug(`joined expo ${exposicionId}`);
    } catch (error) {
      this.logError(`JoinExpo(${exposicionId}) failed`, error);
    }
  }

  private async leaveExpo(exposicionId: number): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      if (this.joinedExpoId === exposicionId) {
        this.joinedExpoId = null;
      }
      return;
    }

    try {
      await this.connection.invoke('LeaveExpo', exposicionId);
      this.logDebug(`left expo ${exposicionId}`);
    } catch (error) {
      if (!this.isConnectionClosedInvocationError(error)) {
        this.logError(`LeaveExpo(${exposicionId}) failed`, error);
      }
    } finally {
      if (this.joinedExpoId === exposicionId) {
        this.joinedExpoId = null;
      }
    }
  }

  private resolveHubUrl(): string {
    const rawBase = (environment.URL_BASE ?? '').trim().replace(/\/+$/, '');
    const hubPath = '/hubs/premiacion';

    if (!rawBase) {
      return hubPath;
    }

    if (/^https?:\/\//i.test(rawBase)) {
      try {
        const parsed = new URL(rawBase);
        parsed.pathname = `${parsed.pathname.replace(/\/+$/, '')}${hubPath}`;
        parsed.search = '';
        parsed.hash = '';
        return parsed.toString();
      } catch {
        // fallback below
      }
    }

    const base = rawBase.startsWith('/') ? rawBase : `/${rawBase}`;
    return `${base}${hubPath}`;
  }

  private normalizeExpoId(value: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return 0;
    }

    return Math.trunc(numeric);
  }

  private isConnectionClosedInvocationError(error: unknown): boolean {
    const message =
      (error as { message?: string } | null)?.message ??
      (typeof error === 'string' ? error : '');

    return message.toLowerCase().includes('invocation canceled due to the underlying connection being closed');
  }

  private logDebug(message: string, payload?: unknown): void {
    if (!this.debugEnabled) {
      return;
    }

    if (payload !== undefined) {
      console.log(`[premiacion.ws.debug] ${message}`, payload);
      return;
    }

    console.log(`[premiacion.ws.debug] ${message}`);
  }

  private logError(message: string, payload?: unknown): void {
    if (!this.debugEnabled) {
      return;
    }

    if (payload !== undefined) {
      console.error(`[premiacion.ws.debug] ${message}`, payload);
      return;
    }

    console.error(`[premiacion.ws.debug] ${message}`);
  }
}
